/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Create Workspace Modal Dialog Component (Task 10.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUiStore } from '../../stores/useUiStore';
import { useWorkspaceStore } from '../../stores/useWorkspaceStore';
import { switchWorkspace } from '../../lib/workspace-switch';
import { getWorkspaceDomains } from '../../lib/urls';
import { X, Building2, User, Users, Sparkles } from 'lucide-react';

export const CreateWorkspaceModal: React.FC = () => {
  const { activeModal, closeModal } = useUiStore();
  const { createWorkspace } = useWorkspaceStore();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<'personal' | 'team'>('team');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (activeModal !== 'create_workspace') {
    return null;
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    // Auto-generate clean slug
    const generatedSlug = val
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setSlug(generatedSlug);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    const newId = `ws_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 6)}`;
    const finalSlug = slug.trim() || `ws-${Date.now().toString(36)}`;

    const newWs = {
      id: newId,
      name: name.trim(),
      slug: finalSlug,
      role: 'OWNER' as const,
      type,
    };

    createWorkspace(newWs);
    switchWorkspace(newId, queryClient);

    setIsSubmitting(false);
    closeModal();
    setName('');
    setSlug('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* 1. Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity animate-fade-in"
        onClick={closeModal}
        aria-hidden="true"
      />

      {/* 2. Modal Card */}
      <div className="relative w-full max-w-md rounded-2xl bg-surface border border-surface-border shadow-2xl p-6 space-y-6 z-10 animate-fade-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-foreground">Create New Workspace</h3>
              <p className="text-xs text-foreground-muted">Set up an isolated multi-tenant environment</p>
            </div>
          </div>
          <button
            onClick={closeModal}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Workspace Type Selector */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground">Workspace Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setType('personal')}
                className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-medium transition-all ${
                  type === 'personal'
                    ? 'border-primary bg-primary/5 text-primary shadow-sm ring-1 ring-primary/30'
                    : 'border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/40'
                }`}
              >
                <User className="w-4 h-4 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold text-foreground">Personal</div>
                  <div className="text-[10px] text-foreground-muted">Individual work</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setType('team')}
                className={`flex items-center gap-2.5 p-3 rounded-xl border text-xs font-medium transition-all ${
                  type === 'team'
                    ? 'border-primary bg-primary/5 text-primary shadow-sm ring-1 ring-primary/30'
                    : 'border-surface-border text-foreground-muted hover:text-foreground hover:bg-muted/40'
                }`}
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold text-foreground">Team / Studio</div>
                  <div className="text-[10px] text-foreground-muted">Collaborative</div>
                </div>
              </button>
            </div>
          </div>

          {/* Name Field */}
          <div className="space-y-1.5">
            <label htmlFor="ws-name" className="text-xs font-semibold text-foreground">
              Workspace Name
            </label>
            <input
              id="ws-name"
              type="text"
              required
              placeholder="e.g. Acme Studio, AI Marketing Hub"
              value={name}
              onChange={handleNameChange}
              className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-background-base text-foreground text-xs focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            />
          </div>

          {/* Slug Field */}
          <div className="space-y-1.5">
            <label htmlFor="ws-slug" className="text-xs font-semibold text-foreground">
              Workspace Identifier (Slug)
            </label>
            <div className="flex items-center rounded-xl border border-surface-border bg-background-base px-3.5 py-2 text-xs font-mono text-foreground-muted">
              <span className="text-foreground-muted/60 select-none">{getWorkspaceDomains().appOrigin.replace(/^https?:\/\//, '')}/</span>
              <input
                id="ws-slug"
                type="text"
                placeholder="acme-studio"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="flex-1 bg-transparent border-none text-foreground text-xs focus:outline-none ml-1"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2.5 rounded-xl border border-surface-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary-hover shadow-glow-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Create Workspace</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
